import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { BottomSheet } from "@/components/BottomSheet";
import { Chips, Field, BigInput } from "@/components/form";

export function RoomSelect({ rooms, value, onChange, testId }: { rooms: any[]; value: string; onChange: (v: string) => void; testId: string }) {
  return (
    <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
      <SelectTrigger data-testid={testId} className="h-12 rounded-xl bg-white"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="none">Not a specific room</SelectItem>
        {rooms.map((r) => <SelectItem key={r.id} value={r.id} data-testid={`${testId}-${r.name}`}>Room {r.name} · {r.sharing === 1 ? "Single" : `${r.sharing}-sharing`}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export function PhotoEditSheet({ propertyId, photo, rooms, src, onClose, onSaved }: { propertyId: string; photo: any; rooms: any[]; src: string; onClose: () => void; onSaved: (p: any) => void }) {
  const [f, setF] = useState({ caption: "", kind: "room", room_id: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (photo) setF({ caption: photo.caption || "", kind: photo.kind, room_id: photo.room_id || "" }); }, [photo]);
  if (!photo) return null;
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/properties/${propertyId}/photos/${photo.id}`, {
        caption: f.caption, kind: f.kind, room_id: f.room_id || null, clear_room: !f.room_id,
      });
      toast.success("Photo updated");
      onSaved(r.data);
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <BottomSheet open={!!photo} onOpenChange={(o) => !o && onClose()} title="Edit photo" testId="photo-edit-sheet">
      <div className="space-y-4">
        <img src={src} alt="" className="w-full aspect-[4/3] object-cover rounded-2xl bg-slate-100" />
        <Field label="Caption" hint={`${f.caption.length}/80`}>
          <BigInput data-testid="photo-edit-caption-input" maxLength={80} value={f.caption} onChange={(e) => setF({ ...f, caption: e.target.value })} placeholder="e.g. Room 101 - 2 sharing" />
        </Field>
        <Field label="Type">
          <Chips testId="photo-edit-kind" value={f.kind} onChange={(v) => setF({ ...f, kind: v, room_id: v === "common" ? "" : f.room_id })} options={[{ value: "room", label: "Room" }, { value: "common", label: "Common area" }]} />
        </Field>
        {f.kind === "room" && (
          <Field label="Which room?" hint="Residents see this photo with the room's price and free beds.">
            <RoomSelect rooms={rooms} value={f.room_id} onChange={(v) => setF({ ...f, room_id: v })} testId="photo-edit-room-select" />
          </Field>
        )}
        <Button data-testid="photo-edit-save-btn" disabled={busy} onClick={save} className="w-full h-14 rounded-xl text-base bg-emerald-700 hover:bg-emerald-800">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save"}
        </Button>
      </div>
    </BottomSheet>
  );
}
